import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
} from '@angular/core';
import { IonicModule } from '@ionic/angular';

import {
    alertService,
    languageService,
    languageSupport,
    toastService,
    viewService,
} from '@unpispas/upp-base';
import type { Pinpad, PinpadPay, PlaceView, TicketView } from '@unpispas/upp-data';
import type { PinpadRefundRequest } from '@unpispas/feature/drivers';
import { UppWdgtModule } from '@unpispas/upp-wdgt';

import {
    UppPinpadPaymentComponent,
    type PinpadAction,
    type PinpadDone,
} from '../payment/upp-payment';
import {
    UppPinpadSelectComponent,
    type ChargeCandidate,
} from '../select/upp-select';

/** Linked or loose refund plan supplied by the ticket host (domain already resolved). */
export type PinpadRefundPlan = {
    origin: PinpadPay | null;
    terminal: Pinpad | null;
};

/** Parameters for {@link UppPinpadSessionComponent.Refund}. */
export type PinpadRefundSessionInput = {
    ticket: TicketView;
    place: PlaceView;
    /** Card amount in currency units (alert copy). */
    amount: number;
    plan: PinpadRefundPlan;
};

/**
 * Outcome of a refund session for the ticket host.
 * `CANCELLED` → do not mutate; `MANUAL` → register return without panel;
 * `DONE` → map `PinpadDone` with domain rules (`RefundCovers`, etc.).
 */
export type PinpadRefundSessionResult =
    | { kind: 'CANCELLED' }
    | { kind: 'MANUAL' }
    | { kind: 'DONE'; done: PinpadDone };

type RefundDialogState = {
    ticket: TicketView;
    terminal: Pinpad;
    request: PinpadRefundRequest;
    action: PinpadAction;
};

/**
 * Visual Paytef session host: terminal resolve, picker, charge/refund dialogs,
 * and refund way alert. Ticket/paycharge call {@link Charge} / {@link Refund}.
 */
@Component({
    selector: 'upp-pinpad-session',
    standalone: true,
    imports: [
        CommonModule,
        IonicModule,
        UppWdgtModule,
        UppPinpadPaymentComponent,
        UppPinpadSelectComponent,
    ],
    templateUrl: './upp-session.html',
    styleUrls: ['./upp-session.scss'],
})
export class UppPinpadSessionComponent extends languageSupport {
    /** Tickets bound to the sale payment dialog (null when closed). */
    PayPPadTickets: TicketView[] | null = null;
    /** Terminal for the in-flight payment dialog. */
    ChargeTerminal: Pinpad | null = null;
    /** Open terminal picker rows; null when closed. */
    TerminalPickOptions: ChargeCandidate[] | null = null;
    /** Refund payment dialog state. */
    RefundDialog: RefundDialogState | null = null;

    private _busy = false;
    private _payResolve: ((ok: boolean) => void) | null = null;
    private _refundResolve: ((done: PinpadDone) => void) | null = null;
    private _terminalPickResolve: ((terminal: Pinpad | null) => void) | null = null;

    constructor(
        lang: languageService,
        private change: ChangeDetectorRef,
        public view: viewService,
        private alertCtrl: alertService,
        private toast: toastService,
    ) {
        super(lang, change);
    }

    /**
     * Runs a SALE session: resolve terminal → optional picker → payment dialog.
     * @returns `true` when APPROVED/MANUAL and settle applied; otherwise `false`.
     */
    async Charge(tickets: TicketView[], place: PlaceView): Promise<boolean> {
        if (this._busy || !tickets?.length || !place) {
            return false;
        }

        const _terminal = await this._ResolveChargePinpad(place);
        if (!_terminal) {
            return false;
        }

        this._busy = true;
        this.ChargeTerminal = _terminal;
        this.PayPPadTickets = tickets;
        this.change.markForCheck();
        return new Promise((resolve) => {
            this._payResolve = resolve;
        });
    }

    /**
     * Runs a REFUND session: way alert → optional resolve/picker → optional refund panel.
     */
    async Refund(input: PinpadRefundSessionInput): Promise<PinpadRefundSessionResult> {
        if (this._busy || !input?.ticket || !input?.place) {
            return { kind: 'CANCELLED' };
        }

        const _way = await this._PickRefundWay(input.place, input.plan, input.amount);
        if (_way === null) {
            return { kind: 'CANCELLED' };
        }

        let _terminal = input.plan.terminal;
        if (_way === 'TERMINAL' && !_terminal && input.plan.origin === null) {
            _terminal = await this._ResolveChargePinpad(input.place);
            if (!_terminal) {
                return { kind: 'CANCELLED' };
            }
        }

        const _request: PinpadRefundRequest = {
            amountCents: Math.round(input.amount * 100),
            origin: input.plan.origin,
        };

        if (_terminal && (_way === 'TERMINAL' || input.ticket.RefundPending)) {
            const _done = await this._OpenRefund(
                input.ticket,
                _terminal,
                _request,
                _way === 'TERMINAL' ? 'RETRY' : 'MANUAL',
            );
            return { kind: 'DONE', done: _done };
        }

        return { kind: 'MANUAL' };
    }

    /******** template handlers ********/

    OnTerminalPick(terminal: Pinpad): void {
        this._FinishTerminalPick(terminal);
    }

    OnTerminalPickCancel(): void {
        this._FinishTerminalPick(null);
    }

    OnPinpadDone(done: PinpadDone): void {
        void this._CompleteCharge(done);
    }

    OnRefundPinpadDone(done: PinpadDone): void {
        const resolve = this._refundResolve;
        this._refundResolve = null;
        this.RefundDialog = null;
        this.ChargeTerminal = null;
        this._busy = false;
        this.change.markForCheck();

        if (done.notice) {
            this.toast.ShowAlert('warning', this._lang.tr(done.notice), 6000);
        }

        resolve?.(done);
    }

    /******** resolve / picker ********/

    /**
     * Resolves the destination terminal without storing it on Place:
     * `OwnPinpad` on ptf; sole pinpad or picker when several exist.
     */
    private async _ResolveChargePinpad(place: PlaceView): Promise<Pinpad | null> {
        const _own = place.OwnPinpad;
        if (_own) {
            return _own;
        }

        const _pads = place.pinpads;
        if (!_pads?.length) {
            return null;
        }

        if (_pads.length === 1) {
            return _pads[0];
        }

        return this._PickChargePinpad(UppPinpadSelectComponent.BuildOptions(place));
    }

    private _PickChargePinpad(options: ChargeCandidate[]): Promise<Pinpad | null> {
        this.TerminalPickOptions = options;
        this.change.markForCheck();
        return new Promise((resolve) => {
            this._terminalPickResolve = resolve;
        });
    }

    private _FinishTerminalPick(terminal: Pinpad | null): void {
        const resolve = this._terminalPickResolve;
        this._terminalPickResolve = null;
        this.TerminalPickOptions = null;
        this.change.markForCheck();
        resolve?.(terminal);
    }

    /******** charge complete ********/

    private async _CompleteCharge(done: PinpadDone): Promise<void> {
        if (done.notice) {
            this.toast.ShowAlert('warning', this._lang.tr(done.notice), 6000);
        }

        const _ok = (done.outcome === 'APPROVED' || done.outcome === 'MANUAL')
            && done.applied !== false;
        this._FinishCharge(_ok);
    }

    private _FinishCharge(ok: boolean): void {
        const resolve = this._payResolve;
        this._payResolve = null;
        this.PayPPadTickets = null;
        this.ChargeTerminal = null;
        this._busy = false;
        this.change.markForCheck();
        resolve?.(ok);
    }

    /******** refund panel / way ********/

    private _OpenRefund(
        ticket: TicketView,
        terminal: Pinpad,
        request: PinpadRefundRequest,
        action: PinpadAction,
    ): Promise<PinpadDone> {
        if (this._busy) {
            return Promise.resolve({ outcome: 'CLOSED', result: null });
        }

        this._busy = true;
        this.RefundDialog = { ticket, terminal, request, action };
        this.change.markForCheck();
        return new Promise((resolve) => {
            this._refundResolve = resolve;
        });
    }

    /**
     * Refund choice alert: terminal / manual / cancel.
     * Drops the terminal option when the sale terminal could not be resolved.
     */
    private async _PickRefundWay(
        place: PlaceView,
        plan: PinpadRefundPlan,
        amount: number,
    ): Promise<'TERMINAL' | 'MANUAL' | null> {
        const _terminal = plan.terminal;
        const _canTerminal = !!_terminal
            || (plan.origin === null && (place.pinpads?.length ?? 0) > 0);

        let _message = _canTerminal
            ? this._lang.tr('@ticket_refund_choose_message', [amount.toFixed(2), this._lang.tr('@currency')])
            : this._lang.tr('@ticket_refund_terminal_unavailable');
        if (_terminal && plan.origin) {
            _message += '<br>' + this._lang.tr('@ticket_refund_choose_linked', [_terminal.PinpadName || '']);
        }

        const _alert = this.alertCtrl.alert({
            header: this._lang.tr('@ticket_refund_choose_title'),
            message: _message,
            backdropDismiss: false,
            buttons: [
                ...(_canTerminal ? [{
                    text: this._lang.tr('@ticket_refund_choose_terminal'),
                    handler: () => {
                        setTimeout(() => { _alert.dismiss('TERMINAL'); }, 100);
                        return false;
                    },
                }] : []),
                {
                    text: this._lang.tr('@ticket_refund_choose_manual'),
                    handler: () => {
                        setTimeout(() => { _alert.dismiss('MANUAL'); }, 100);
                        return false;
                    },
                },
                {
                    text: this._lang.tr('@place_ticket_cancel'),
                    role: 'cancel',
                    handler: () => {
                        setTimeout(() => { _alert.dismiss(null); }, 100);
                        return false;
                    },
                },
            ],
        });

        await _alert.present();
        const _detail = await _alert.onDidDismiss();
        const _data = _detail?.data;
        if (_data === 'TERMINAL' || _data === 'MANUAL') {
            return _data;
        }
        return null;
    }
}
